import { useCallback, useEffect, useRef, useState } from "react";
import {
  useLiveKitReconnect,
  type ChatWidgetConfig,
} from "@hopper-ai/chat-widget";
import { LiveKitChatProvider } from "@hopper-ai/livekit-chat-provider";
import { Experiments, useExperiment } from "@tenant/experiments";
import { clearsInitialLoading } from "./htsAssistChatReadiness";
import {
  generateHtsAssistToken,
  type GenerateHtsAssistTokenRequest,
} from "@/api/htsAssist/generateToken";
import { submitHtsAssistCsat } from "@/api/htsAssist/submitCsat";

/** Shared lifecycle for floating and standalone chat. Keep request stable for a session. */
export function useHtsAssistSession(
  enabled: boolean,
  request: GenerateHtsAssistTokenRequest,
  onClose: () => void
) {
  const { isExperimentVariant } = useExperiment();
  const csatEnabled = isExperimentVariant(
    Experiments.HTS_ASSIST_CSAT_ENABLED.name,
    Experiments.HTS_ASSIST_CSAT_ENABLED.variants.available
  );
  const [chatProvider, setChatProvider] = useState<LiveKitChatProvider>();
  const [showInitialLoadingComponent, setShowInitialLoadingComponent] =
    useState(false);
  const [hasError, setHasError] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const generationRef = useRef(0);
  const requestRef = useRef(request);
  const actionPendingRef = useRef<number | null>(null);
  const closedRef = useRef(false);
  const { endSession } = useLiveKitReconnect(chatProvider);

  const startSession = useCallback(
    async (nextRequest: GenerateHtsAssistTokenRequest) => {
      const generation = ++generationRef.current;
      requestRef.current = nextRequest;
      setChatProvider(undefined);
      setSessionKey(generation);
      setHasError(false);
      setShowInitialLoadingComponent(true);
      try {
        const { token, url } = await generateHtsAssistToken(nextRequest);
        if (generation !== generationRef.current) return;
        setChatProvider(
          new LiveKitChatProvider({
            url,
            tokenProvider: async () => token,
            conversationMode: "text",
          })
        );
      } catch {
        if (generation !== generationRef.current) return;
        setHasError(true);
        setShowInitialLoadingComponent(false);
        console.error("[HtsAssist] Failed to fetch LiveKit token");
      }
    },
    []
  );

  const invalidateSession = useCallback(() => {
    closedRef.current = true;
    ++generationRef.current;
  }, []);

  useEffect(() => {
    closedRef.current = !enabled;
    actionPendingRef.current = null;
    if (enabled) {
      void startSession(request);
    } else {
      setChatProvider(undefined);
      setShowInitialLoadingComponent(false);
      setHasError(false);
    }
    return invalidateSession;
  }, [enabled, request, startSession, invalidateSession]);

  useEffect(() => {
    if (!chatProvider) return undefined;
    return chatProvider.subscribe((event) => {
      if (clearsInitialLoading(event)) setShowInitialLoadingComponent(false);
    });
  }, [chatProvider]);

  const restart = async (freshConversation: boolean) => {
    if (closedRef.current || actionPendingRef.current !== null) return;
    const generation = generationRef.current;
    actionPendingRef.current = generation;
    if (freshConversation) {
      // Retain the original booking/entry point, without replaying the initial intent.
      const { entryAction, initialUserQuery, ...context } = requestRef.current;
      requestRef.current = context;
    }
    try {
      await endSession();
      if (closedRef.current || generation !== generationRef.current) return;
      await startSession(requestRef.current);
    } catch {
      if (closedRef.current || generation !== generationRef.current) return;
      setHasError(true);
      console.error("[HtsAssist] Failed to end the previous chat session");
    } finally {
      if (actionPendingRef.current === generation)
        actionPendingRef.current = null;
    }
  };

  const closeChat = async () => {
    if (closedRef.current) return;
    closedRef.current = true;
    const generation = ++generationRef.current;
    try {
      await endSession();
    } catch {
      console.error("[HtsAssist] Failed to end the chat session");
    } finally {
      if (generation === generationRef.current) {
        setChatProvider(undefined);
        onClose();
      }
    }
  };

  const csat: ChatWidgetConfig["csat"] = {
    enabled: csatEnabled,
    onSubmit: submitHtsAssistCsat,
    onStartNewChat: () => {
      void restart(true);
    },
    onCloseChat: () => {
      void closeChat();
    },
  };

  return {
    chatProvider,
    showInitialLoadingComponent,
    hasError,
    sessionKey,
    csat,
    closeChat,
    retry: () => {
      void restart(false);
    },
  };
}
